// Piece tracking, elimination, and position management
// All functions take game state as parameters (no React dependency)

import { isKingRole, getPieceTypeFromRole } from './gameUtils';

// Find the piece at a given square using pieces data
// capturedPieceType/capturedColor: fallback lookup when currentPosition is out of sync
export const findPieceAtSquare = (pieces, moveHistory, square, capturedPieceType = null, capturedColor = null) => {
  if (!pieces) return null;
  const squareLower = square.toLowerCase();

  // First, try to find by currentPosition (preferred method)
  const pieceByPosition = Object.values(pieces).find(p =>
    p.currentPosition === squareLower && !p.captured
  );

  if (pieceByPosition) {
    return pieceByPosition;
  }

  // If not found by currentPosition and we know the captured piece type,
  // try to find by piece type (for out-of-sync pieces data)
  if (capturedPieceType && capturedColor) {
    const pieceTypeMap = { p: 'pawn', n: 'knight', b: 'bishop', r: 'rook', q: 'queen', k: 'king' };
    const fullType = pieceTypeMap[capturedPieceType] || capturedPieceType;
    const fullColor = capturedColor === 'w' ? 'white' : (capturedColor === 'b' ? 'black' : capturedColor);

    const matchingPieces = Object.values(pieces).filter(p =>
      p.type === fullType &&
      p.color === fullColor &&
      !p.captured
    );

    if (matchingPieces.length === 1) {
      console.log(`Found piece ${matchingPieces[0].id} at ${squareLower} via piece type lookup`);
      return matchingPieces[0];
    } else if (matchingPieces.length > 1) {
      // Multiple matching pieces - use moveHistory to determine which piece moved where
      const history = moveHistory || [];
      const piecePositions = {};
      matchingPieces.forEach(p => {
        piecePositions[p.id] = p.startPosition;
      });

      // Replay moves to track positions
      history.forEach(move => {
        const movedPieceId = Object.entries(piecePositions).find(([id, pos]) =>
          pos === move.from.toLowerCase()
        )?.[0];
        if (movedPieceId) {
          piecePositions[movedPieceId] = move.to.toLowerCase();
        }
      });

      // Find piece that should be at the target square
      const pieceAtSquare = Object.entries(piecePositions).find(([id, pos]) =>
        pos === squareLower && matchingPieces.some(p => p.id === id)
      );

      if (pieceAtSquare) {
        const foundPiece = matchingPieces.find(p => p.id === pieceAtSquare[0]);
        console.log(`Found piece ${foundPiece.id} at ${squareLower} via move history tracking`);
        return foundPiece;
      }

      // Try to find by startPosition (if piece never moved)
      const byStartPos = matchingPieces.find(p => p.startPosition === squareLower);
      if (byStartPos) {
        console.log(`Found piece ${byStartPos.id} at ${squareLower} by startPosition match`);
        return byStartPos;
      }

      // For unique pieces like queen, just return the one
      if (fullType === 'queen' || fullType === 'king') {
        return matchingPieces[0];
      }

      console.warn(`Multiple ${fullType}s found (${matchingPieces.map(p => p.id).join(', ')}), cannot determine which was at ${squareLower}`);
      return matchingPieces[0];
    }
  }

  return null;
};

// Find piece for en passant capture (pawn captured is not on target square)
export const findEnPassantCapturedPiece = (pieces, captureSquare, capturedColor) => {
  if (!pieces) return null;

  const file = captureSquare.charAt(0);
  const capturedPawnRank = capturedColor === 'b' ? '5' : '4';
  const capturedPawnSquare = `${file}${capturedPawnRank}`;

  return Object.values(pieces).find(p =>
    p.currentPosition === capturedPawnSquare &&
    p.type === 'pawn' &&
    p.color === (capturedColor === 'b' ? 'black' : 'white') &&
    !p.captured
  );
};

// Find a player to eliminate when a piece is captured at a square
// Returns { playerEmail, pieceId, piece }
export const findPlayerToEliminateBySquare = (game, captureSquare, isEnPassant = false, capturedColor = null, capturedPieceType = null) => {
  if (!game?.pieces) {
    console.warn('No pieces data available for elimination lookup');
    return { playerEmail: null, pieceId: null, piece: null };
  }

  // Kings cannot be captured
  if (capturedPieceType === 'k') {
    console.warn('CRITICAL: Attempted to eliminate King - this should never happen!');
    return { playerEmail: null, pieceId: null, piece: null };
  }

  const squareLower = captureSquare.toLowerCase();
  let capturedPiece;

  if (isEnPassant && capturedColor) {
    capturedPiece = findEnPassantCapturedPiece(game.pieces, captureSquare, capturedColor);
  } else {
    capturedPiece = findPieceAtSquare(game.pieces, game.moveHistory, captureSquare, capturedPieceType, capturedColor);
  }

  if (!capturedPiece) {
    console.warn(`No piece found at capture square ${squareLower}. Current pieces positions:`);
    Object.values(game.pieces).forEach(p => {
      if (!p.captured) {
        console.log(`  ${p.id}: ${p.currentPosition} (started at ${p.startPosition})`);
      }
    });
    return { playerEmail: null, pieceId: null, piece: null };
  }

  // Never eliminate a King
  if (capturedPiece.type === 'king') {
    console.warn('CRITICAL: Captured piece is a King - preventing elimination!');
    return { playerEmail: null, pieceId: null, piece: null };
  }

  const pieceId = capturedPiece.id;
  const pieceColor = capturedPiece.color;
  const team = pieceColor === 'white' ? game.whiteTeam : game.blackTeam;

  // Find the player who controls this specific piece by pieceId
  const playerWithPiece = team?.find(p => p.pieceId === pieceId);

  if (playerWithPiece) {
    if (isKingRole(playerWithPiece.role)) {
      console.warn(`CRITICAL: Player ${playerWithPiece.nickname} has King role - preventing elimination!`);
      return { playerEmail: null, pieceId: pieceId, piece: capturedPiece };
    }
    console.log(`Found player ${playerWithPiece.nickname} (${playerWithPiece.email}) for piece ${pieceId} at ${squareLower}`);
    return { playerEmail: playerWithPiece.email, pieceId, piece: capturedPiece };
  }

  // Fallback to piece's stored playerEmail
  if (capturedPiece.playerEmail) {
    const playerByEmail = team?.find(p => p.email === capturedPiece.playerEmail);
    if (isKingRole(playerByEmail?.role)) {
      console.warn(`CRITICAL: Player ${playerByEmail.nickname} has King role - preventing elimination!`);
      return { playerEmail: null, pieceId: pieceId, piece: capturedPiece };
    }
    console.log(`Using piece's stored playerEmail: ${capturedPiece.playerEmail} for piece ${pieceId}`);
    return { playerEmail: capturedPiece.playerEmail, pieceId, piece: capturedPiece };
  }

  console.warn(`No player found for captured piece ${pieceId} at ${squareLower}`);
  return { playerEmail: null, pieceId: null, piece: capturedPiece };
};

// Legacy elimination function for old games without pieces data
export const findPlayerToEliminateLegacy = (game, capturedPieceType, capturedColor, targetSquare = null) => {
  if (!game) return null;

  if (capturedPieceType === 'k') {
    console.warn('CRITICAL: Attempted to eliminate King via legacy method!');
    return null;
  }

  const team = capturedColor === 'w' ? game.whiteTeam : game.blackTeam;
  const eliminatedPlayers = game.eliminatedPlayers || [];

  // Try piece tracking first if available
  if (game.pieces && targetSquare) {
    const piece = findPieceAtSquare(game.pieces, game.moveHistory, targetSquare);
    if (piece && piece.id) {
      if (piece.type === 'king') {
        console.warn('CRITICAL: Captured piece is a King - preventing elimination!');
        return null;
      }
      const playerByPieceId = team?.find(p => p.pieceId === piece.id);
      if (playerByPieceId && !eliminatedPlayers.includes(playerByPieceId.email)) {
        if (isKingRole(playerByPieceId.role)) {
          console.warn(`CRITICAL: Player ${playerByPieceId.nickname} has King role - preventing elimination!`);
          return null;
        }
        return playerByPieceId.email;
      }
    }
  }

  // Fallback: match by piece type (imprecise)
  console.warn('Using imprecise role-based elimination fallback.');
  const player = team?.find(p => {
    if (eliminatedPlayers.includes(p.email)) return false;
    if (isKingRole(p.role)) return false;
    const pieceType = getPieceTypeFromRole(p.role);
    return pieceType === capturedPieceType;
  });

  return player?.email || null;
};

// Update pieces data after a move (positions + capture status)
// Handles castling
export const getUpdatedPiecesAfterMove = (pieces, fromSquare, toSquare, capturedPieceId = null) => {
  if (!pieces) return null;

  const updatedPieces = JSON.parse(JSON.stringify(pieces));
  const fromLower = fromSquare.toLowerCase();
  const toLower = toSquare.toLowerCase();

  // Find the moving piece
  const movingPiece = Object.values(updatedPieces).find(p =>
    p.currentPosition === fromLower && !p.captured
  );

  if (movingPiece) {
    updatedPieces[movingPiece.id] = {
      ...updatedPieces[movingPiece.id],
      currentPosition: toLower
    };

    // Handle castling - also move the rook
    if (movingPiece.type === 'king') {
      const fromFile = fromLower.charAt(0);
      const toFile = toLower.charAt(0);
      const rank = fromLower.charAt(1);
      const colorPrefix = movingPiece.color === 'white' ? 'w' : 'b';

      // Kingside castling (king moves from e to g)
      if (fromFile === 'e' && toFile === 'g') {
        const rookId = `${colorPrefix}r2`;
        if (updatedPieces[rookId] && !updatedPieces[rookId].captured) {
          updatedPieces[rookId] = {
            ...updatedPieces[rookId],
            currentPosition: `f${rank}`
          };
        }
      }
      // Queenside castling (king moves from e to c)
      else if (fromFile === 'e' && toFile === 'c') {
        const rookId = `${colorPrefix}r1`;
        if (updatedPieces[rookId] && !updatedPieces[rookId].captured) {
          updatedPieces[rookId] = {
            ...updatedPieces[rookId],
            currentPosition: `d${rank}`
          };
        }
      }
    }
  }

  // Mark captured piece
  if (capturedPieceId && updatedPieces[capturedPieceId]) {
    updatedPieces[capturedPieceId] = {
      ...updatedPieces[capturedPieceId],
      captured: true,
      capturedAt: new Date().toISOString()
    };
  }

  return updatedPieces;
};

// Sync pieces data with actual board position from FEN
export const syncPiecesWithBoard = (pieces, chess) => {
  if (!pieces) return pieces;

  const board = chess.board();
  const files = ['a', 'b', 'c', 'd', 'e', 'f', 'g', 'h'];
  const pieceTypeMap = { p: 'pawn', n: 'knight', b: 'bishop', r: 'rook', q: 'queen', k: 'king' };

  const boardPositions = {};
  for (let rank = 0; rank < 8; rank++) {
    for (let file = 0; file < 8; file++) {
      const piece = board[rank][file];
      if (piece) {
        const square = `${files[file]}${8 - rank}`;
        const color = piece.color === 'w' ? 'white' : 'black';
        const type = pieceTypeMap[piece.type];
        boardPositions[square] = { color, type };
      }
    }
  }

  const syncedPieces = JSON.parse(JSON.stringify(pieces));
  let needsSync = false;

  Object.values(syncedPieces).forEach(piece => {
    if (piece.captured) return;

    const boardPiece = boardPositions[piece.currentPosition];
    if (!boardPiece || boardPiece.color !== piece.color || boardPiece.type !== piece.type) {
      const actualSquare = Object.entries(boardPositions).find(([sq, bp]) => {
        if (bp.color !== piece.color || bp.type !== piece.type) return false;
        const otherPiece = Object.values(syncedPieces).find(p =>
          p.id !== piece.id && p.currentPosition === sq && !p.captured
        );
        return !otherPiece;
      });

      if (actualSquare) {
        console.log(`Syncing piece ${piece.id}: ${piece.currentPosition} -> ${actualSquare[0]}`);
        syncedPieces[piece.id].currentPosition = actualSquare[0];
        needsSync = true;
      } else if (!boardPiece) {
        console.log(`Piece ${piece.id} not found on board at ${piece.currentPosition} - may be captured`);
      }
    }
  });

  if (needsSync) {
    console.log('Pieces data was out of sync and has been corrected locally');
  }

  return syncedPieces;
};
